import React, { useMemo, useState, useEffect } from "react";
import { useNavigation, useTranslate } from "@refinedev/core";
import { DateField } from "@refinedev/mui";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Paper,
  CircularProgress,
  Switch,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from "@mui/lab";
import dayjs from "dayjs";
import axiosInstance from "../../api/axios-config";
import { useAuth } from "../../contexts/auth-context";
import { useIPLocation } from "../../hooks/useIPLocation";

// Import leaflet CSS and fix marker icons
import 'leaflet/dist/leaflet.css';
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export interface IAlert {
  id: number;
  user: {
    id: number;
    fullname: string;
    email: string;
    profilePicture?: string;
    phone?: string;
    entrepriseId?: number;
  };
  latitude: number;
  longitude: number;
  createdAt: string;
  updatedAt: string;
  isBroadcasting: boolean;
  status: 'NEW' | 'ACCEPTED' | 'CLOSED' | 'CALLED';
}

// Custom marker styles
const customMarkerStyles = `
  .custom-alert-marker {
    background: transparent !important;
    border: none !important;
  }

  .alert-marker-content {
    transition: transform 0.2s ease;
  }

  .custom-alert-marker:hover {
    z-index: 1000 !important;
  }

  .custom-alert-marker:hover .alert-marker-content {
    transform: scale(1.1);
  }

  .alert-marker-content img {
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.8;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes emergencyPulse {
    0% {
      box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7);
    }
    70% {
      box-shadow: 0 0 0 15px rgba(255, 0, 0, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(255, 0, 0, 0);
    }
  }

  .emergency-pulse {
    animation: emergencyPulse 2s infinite;
  }

  .alert-pulse {
    animation: pulse 2s ease-in-out infinite;
  }
`;

// Helper function to create custom marker HTML
const createAlertMarkerHtml = (alert: IAlert, isActive: boolean) => {
  const statusColors = {
    'NEW': '#2196F3',        // Blue
    'ACCEPTED': '#FF9800',   // Orange
    'CALLED': '#9C27B0',     // Purple
    'CLOSED': '#4CAF50'      // Green
  };

  const statusIcons = {
    'NEW': '🆕',
    'ACCEPTED': '⚡',
    'CALLED': '📞',
    'CLOSED': '✅'
  };

  const color = statusColors[alert.status] || '#757575';
  const icon = statusIcons[alert.status] || '❓';
  const pulseClass = isActive ? 'emergency-pulse' : '';

  return `
    <div class="alert-marker-content" style="position: relative; width: 60px; height: 60px;">
      <!-- Main marker circle -->
      <div class="${pulseClass}" style="
        position: absolute;
        top: 10px;
        left: 10px;
        width: 40px;
        height: 40px;
        background: ${color};
        border-radius: 50%;
        overflow: hidden;
        border: 3px solid ${color};
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
      ">
        <img
          src="${alert.user.profilePicture || `https://api.dicebear.com/7.x/avataaars/svg?seed=${alert.user.id}`}"
          alt="${alert.user.fullname}"
          style="width: 100%; height: 100%; object-fit: cover;"
          onerror="this.src='https://api.dicebear.com/7.x/initials/svg?seed=${alert.user.fullname || alert.user.email}'"
        />
      </div>

      ${alert.status === 'NEW' ? `
        <div style="
          position: absolute;
          top: 0;
          right: 0;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          overflow: hidden;
          border: 2px solid #fff;
          box-shadow: 0 1px 4px rgba(0,0,0,0.3);
          background: ${statusColors[alert.status]};
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
        ">
          ${statusIcons[alert.status]}
        </div>
      ` : ''}

      <!-- Live indicator -->
      ${isActive ? `
        <div class="alert-pulse" style="
          position: absolute;
          bottom: 8px;
          right: 8px;
          width: 12px;
          height: 12px;
          background: #ff0000;
          border-radius: 50%;
          border: 2px solid #fff;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
        "></div>
      ` : ''}

      <!-- Arrow pointing down -->
      <div style="
        position: absolute;
        bottom: 2px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 10px solid ${color};
        filter: drop-shadow(0 2px 3px rgba(0,0,0,0.2));
      "></div>
    </div>
  `;
};

// Alert Card Component
const AlertCard: React.FC<{ alert: IAlert }> = ({ alert }) => {
  const { show } = useNavigation();
  const translate = useTranslate();
  const theme = useTheme();
  const userLocale = navigator.language;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const isActive = alert.isBroadcasting;

  return (
    <Card
      onClick={() => {
        show("monitoring", alert.id);
      }}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: isActive ? '2px solid #ff4444' : '1px solid #e0e0e0',
        borderRadius: 2,
        boxShadow: isActive ? '0 4px 8px rgba(255, 68, 68, 0.2)' : undefined,
        cursor: 'pointer',
        minHeight: { xs: '100px', md: '120px' },
        '&:hover': {
          boxShadow: isActive
            ? '0 8px 16px rgba(255, 68, 68, 0.3)'
            : '0 4px 8px rgba(0,0,0,0.1)',
        }
      }}
    >
      <CardContent sx={{
        flexGrow: 1,
        p: { xs: 1, md: 2 },
        '&:last-child': { pb: { xs: 1, md: 2 } }
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
          mb: { xs: 1, md: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 0.5, sm: 0 }
        }}>
          <Box sx={{ flexGrow: 1, minWidth: 0, mb: { xs: 0.5, sm: 0 } }}>
            <Typography
              variant={isSmallScreen ? "body2" : "subtitle2"}
              noWrap
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              }}
            >
              {alert.user.fullname || alert.user.email}
            </Typography>
          </Box>
          <Box sx={{
            display: 'flex',
            gap: { xs: 0.5, md: 1 },
            flexWrap: 'wrap',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' }
          }}>
            <Chip
              label={translate(`monitoring.status.${alert.status.toLowerCase()}`)}
              color={
                alert.status === 'NEW' ? 'info' :
                alert.status === 'ACCEPTED' ? 'warning' :
                alert.status === 'CALLED' ? 'secondary' : 'success'
              }
              size="small"
              sx={{
                fontSize: { xs: '0.6rem', md: '0.75rem' },
                height: { xs: '20px', md: '24px' }
              }}
            />
            <Chip
              label={isActive ? translate("monitoring.live") : translate("monitoring.ended")}
              color={isActive ? "error" : "success"}
              size="small"
              sx={{
                fontSize: { xs: '0.6rem', md: '0.75rem' },
                height: { xs: '20px', md: '24px' }
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: '0.65rem', md: '0.75rem' },
                color: 'text.secondary'
              }}
            >
              <DateField locales={userLocale} value={alert.createdAt} format="DD/MM/YYYY HH:mm" />
            </Typography>
          </Box>
        </Box>

      </CardContent>
    </Card>
  );
};

// Map interaction handler component
const MapInteractionHandler: React.FC<{ onInteraction: () => void }> = ({ onInteraction }) => {
  useMapEvents({
    movestart: () => onInteraction(),
    zoomstart: () => onInteraction(),
  });
  return null;
};

// Map Component with fit bounds functionality
const AlertsMap: React.FC<{ alerts: IAlert[], eventLocation: [number, number] | null, ipLocation: { latitude: number, longitude: number } | null }> = ({ alerts, eventLocation, ipLocation }) => {
  const { show } = useNavigation();
  const translate = useTranslate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const mapRef = React.useRef<any>(null);
  const [showOnlyActive, setShowOnlyActive] = React.useState(false);
  const [hasUserInteracted, setHasUserInteracted] = React.useState(false);

  // Filter alerts based on toggle
  const filteredAlerts = useMemo(() => {
    if (showOnlyActive) {
      return alerts.filter(alert => alert.isBroadcasting);
    }
    return alerts;
  }, [alerts, showOnlyActive]);

  // Calculate map center and bounds based on filtered alerts
  const { mapCenter, bounds } = useMemo(() => {
    // Priorité : eventLocation si disponible
    if (eventLocation) {
      return {
        mapCenter: eventLocation,
        bounds: null,
      };
    }

    if (!filteredAlerts.length) {
      // Utiliser la localisation IP si disponible, sinon Marseille par défaut
      const defaultCenter = ipLocation
        ? [ipLocation.latitude, ipLocation.longitude] as [number, number]
        : [43.2962717, 5.3832961] as [number, number];
      return {
        mapCenter: defaultCenter,
        bounds: null,
      };
    }

    const validAlerts = filteredAlerts.filter((alert) => alert.latitude && alert.longitude);
    if (!validAlerts.length) {
      // Utiliser la localisation IP si disponible, sinon Marseille par défaut
      const defaultCenter = ipLocation
        ? [ipLocation.latitude, ipLocation.longitude] as [number, number]
        : [43.2962717, 5.3832961] as [number, number];
      return {
        mapCenter: defaultCenter,
        bounds: null,
      };
    }

    // Calcul du centre – moyenne des positions
    const avgLat =
      validAlerts.reduce((sum, alert) => sum + alert.latitude, 0) /
      validAlerts.length;
    const avgLng =
      validAlerts.reduce((sum, alert) => sum + alert.longitude, 0) /
      validAlerts.length;

    // Calcul des bounds pour englober tous les marqueurs
    const latitudes = validAlerts.map((alert) => alert.latitude);
    const longitudes = validAlerts.map((alert) => alert.longitude);

    const calculatedBounds = [
      [Math.min(...latitudes), Math.min(...longitudes)],
      [Math.max(...latitudes), Math.max(...longitudes)],
    ] as [[number, number], [number, number]];

    return {
      mapCenter: [avgLat, avgLng] as [number, number],
      bounds: validAlerts.length > 1 ? calculatedBounds : null,
    };
  }, [filteredAlerts, eventLocation, ipLocation]);

  // Fit map to show all markers when they change (only if user hasn't interacted)
  React.useEffect(() => {
    if (mapRef.current && bounds && filteredAlerts.length > 1 && !hasUserInteracted) {
      mapRef.current.fitBounds(bounds, {
        padding: [20, 20],
        maxZoom: 15
      });
    }
  }, [bounds, filteredAlerts.length, hasUserInteracted]);

  return (
    <>
      <style>{customMarkerStyles}</style>
      <Paper sx={{
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{
          p: { xs: 1, md: 2 },
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography
            variant={isSmallScreen ? "subtitle1" : "h6"}
            sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
          >
            {translate("monitoring.alertLocation")}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  color: showOnlyActive ? theme.palette.error.main : theme.palette.text.secondary
                }}
              >
                {showOnlyActive ? translate("monitoring.activeOnly") : translate("monitoring.allAlerts")}
              </Typography>
              <Chip
                label={filteredAlerts.length}
                size="small"
                color={showOnlyActive ? "error" : "default"}
                sx={{
                  fontSize: { xs: '0.65rem', md: '0.75rem' },
                  height: { xs: '20px', md: '24px' },
                  minWidth: '32px'
                }}
              />
            </Box>
            <Switch
              checked={showOnlyActive}
              onChange={(e) => setShowOnlyActive(e.target.checked)}
              size="small"
              color="error"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: theme.palette.error.main,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: theme.palette.error.main,
                },
              }}
            />
          </Box>
        </Box>
        <Box sx={{
          flex: 1,
          minHeight: 0,
          position: 'relative'
        }}>
          <MapContainer
            ref={mapRef}
            center={mapCenter}
            zoom={eventLocation ? 15 : filteredAlerts.length === 1 ? 15 : 12}
            style={{ height: "100%", width: "100%" }}
          >
            <MapInteractionHandler onInteraction={() => setHasUserInteracted(true)} />
            <TileLayer
              attribution='&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url={`https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${process.env.REACT_APP_STADIA_API_KEY}`}
              minZoom={0}
              maxZoom={20}
            />
            {filteredAlerts.map((alert) => {
              const customIcon = L.divIcon({
                html: createAlertMarkerHtml(alert, alert.isBroadcasting),
                iconSize: [60, 60],
                iconAnchor: [30, 50],
                popupAnchor: [0, -50],
                className: 'custom-alert-marker'
              });

              return (
                alert.latitude && alert.longitude && (
                  <Marker
                    key={alert.id}
                    position={[alert.latitude, alert.longitude]}
                    icon={customIcon}
                  >
                    <Popup>
                      <Box sx={{ p: { xs: 1, md: 1.5 }, minWidth: { xs: '200px', md: '250px' } }}>
                        {/* Header with user info */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <img
                            src={alert.user.profilePicture || `https://api.dicebear.com/7.x/avataaars/svg?seed=${alert.user.id}`}
                            alt={alert.user.fullname}
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              marginRight: '12px',
                              border: '2px solid #e0e0e0',
                              objectFit: 'cover'
                            }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant={isSmallScreen ? "body2" : "subtitle2"}
                              sx={{
                                fontSize: { xs: '0.875rem', md: '0.95rem' },
                                fontWeight: 'bold',
                                lineHeight: 1.2,
                                mb: 0.5
                              }}
                            >
                              {alert.user.fullname || alert.user.email}
                            </Typography>
                            <Chip
                              label={translate(`monitoring.status.${alert.status.toLowerCase()}`)}
                              size="small"
                              color={
                                alert.status === 'NEW' ? 'info' :
                                alert.status === 'ACCEPTED' ? 'warning' :
                                alert.status === 'CALLED' ? 'secondary' : 'success'
                              }
                              sx={{
                                fontSize: { xs: '0.6rem', md: '0.7rem' },
                                height: { xs: '18px', md: '20px' },
                                '& .MuiChip-label': {
                                  px: { xs: 0.5, md: 0.75 }
                                }
                              }}
                            />
                          </Box>
                        </Box>

                        {/* Divider */}
                        <Box sx={{
                          borderTop: '1px solid #eee',
                          my: 1
                        }} />

                        {/* Alert info */}
                        <Box sx={{ mb: 1 }}>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                            >
                              🕐 {dayjs(alert.createdAt).format("DD/MM/YYYY HH:mm")}
                            </Typography>
                            {alert.isBroadcasting && (
                              <Chip
                                label={translate("monitoring.live")}
                                color="error"
                                size="small"
                                sx={{
                                  fontSize: { xs: '0.6rem', md: '0.7rem' },
                                  height: { xs: '18px', md: '20px' },
                                  '& .MuiChip-label': {
                                    px: { xs: 0.5, md: 0.75 }
                                  },
                                  animation: 'pulse 2s ease-in-out infinite'
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                          <button
                            onClick={() => show("monitoring", alert.id)}
                            style={{
                              width: '100%',
                              padding: '6px 12px',
                              backgroundColor: theme.palette.primary.main,
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: isSmallScreen ? '0.7rem' : '0.75rem',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = theme.palette.primary.dark}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = theme.palette.primary.main}
                          >
                            {translate("monitoring.viewDetails")}
                          </button>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <a
                              href={`https://www.google.com/maps?q=${alert.latitude},${alert.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                flex: 1,
                                padding: '6px 12px',
                                backgroundColor: '#f5f5f5',
                                color: '#333',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: isSmallScreen ? '0.7rem' : '0.75rem',
                                textDecoration: 'none',
                                transition: 'background-color 0.2s',
                                display: 'inline-block',
                                textAlign: 'center'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            >
                              📍 Maps
                            </a>
                            <a
                              href={`https://www.google.com/maps?q=&layer=c&cbll=${alert.latitude},${alert.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                flex: 1,
                                padding: '6px 12px',
                                backgroundColor: '#f5f5f5',
                                color: '#333',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: isSmallScreen ? '0.7rem' : '0.75rem',
                                textDecoration: 'none',
                                transition: 'background-color 0.2s',
                                display: 'inline-block',
                                textAlign: 'center'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            >
                              🗺️ Street View
                            </a>
                          </Box>
                        </Box>
                      </Box>
                    </Popup>
                  </Marker>
                )
              );
            })}
          </MapContainer>
        </Box>
      </Paper>
    </>
  );
};

// Timeline Component
const AlertTimeline: React.FC<{ alerts: IAlert[] }> = ({ alerts }) => {
  const { show } = useNavigation();
  const translate = useTranslate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Sort alerts by creation date (most recent first)
  const sortedAlerts = useMemo(() => {
    const sorted = [...alerts].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return sorted;
  }, [alerts]);

  // Helper function for status styling
  const getStatusInfo = (status: string, theme: any) => {
    const isDark = theme.palette.mode === 'dark';

    switch (status) {
      case 'NEW':
        return {
          color: 'info',
          label: translate('monitoring.status.new'),
          bgColor: isDark ? theme.palette.info.dark : '#e3f2fd',
          borderColor: theme.palette.info.main
        };
      case 'ACCEPTED':
        return {
          color: 'warning',
          label: translate('monitoring.status.accepted'),
          bgColor: isDark ? theme.palette.warning.dark : '#fff3e0',
          borderColor: theme.palette.warning.main
        };
      case 'CLOSED':
        return {
          color: 'success',
          label: translate('monitoring.status.closed'),
          bgColor: isDark ? theme.palette.success.dark : '#e8f5e9',
          borderColor: theme.palette.success.main
        };
      case 'CALLED':
        return {
          color: 'secondary',
          label: translate('monitoring.status.called'),
          bgColor: isDark ? theme.palette.secondary.dark : '#f3e5f5',
          borderColor: theme.palette.secondary.main
        };
      default:
        return {
          color: 'default',
          label: status,
          bgColor: isDark ? theme.palette.grey[800] : '#f5f5f5',
          borderColor: theme.palette.grey[500]
        };
    }
  };

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{
        p: { xs: 1, md: 2 },
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0
      }}>
        <Typography
          variant={isSmallScreen ? "subtitle1" : "h6"}
          sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
        >
          {translate("monitoring.endedAlertsHistory")}
        </Typography>
      </Box>

      <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: { xs: 0.5, md: 1 },
        '&::-webkit-scrollbar': {
          width: { xs: '4px', md: '6px' },
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: theme.palette.background.default,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.divider,
          borderRadius: '3px',
        },
      }}>
        <Timeline
          position="right"
          sx={{
            mt: 0,
            pt: 0,
            width: "100%",
            "& .MuiTimelineItem-root": {
              minHeight: "auto",
              "&:before": {
                display: "none",
              },
            },
          }}
        >
          {sortedAlerts.slice(0, 20).map((alert, index) => {
            const statusInfo = getStatusInfo(alert.status, theme);

            return (
              <TimelineItem key={alert.id}>
                <TimelineOppositeContent sx={{ display: "none" }} />
                <TimelineSeparator>
                  <TimelineDot variant="outlined" sx={{ borderColor: statusInfo.borderColor }} />
                  {index < sortedAlerts.length - 1 && (
                    <TimelineConnector sx={{ width: "1px" }} />
                  )}
                </TimelineSeparator>
                <TimelineContent
                  sx={{
                    maxWidth: "calc(100% - 32px)",
                    overflow: "hidden",
                    pb: 2,
                  }}
                >
                  <Box
                    onClick={() => {
                      show("monitoring", alert.id);
                    }}
                    sx={{
                      backgroundColor: statusInfo.bgColor,
                      border: `2px solid ${statusInfo.borderColor}`,
                      borderRadius: 2,
                      p: { xs: 1, md: 1.5 },
                      wordWrap: "break-word",
                      maxWidth: "100%",
                      overflow: "hidden",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: `0 2px 8px ${statusInfo.borderColor}66`,
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: { xs: 0.5, md: 0.5 },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 0.5, sm: 0 }
                      }}
                    >
                      <Typography
                        variant={isSmallScreen ? "body2" : "subtitle2"}
                        sx={{
                          color: theme.palette.getContrastText(statusInfo.bgColor),
                          fontWeight: "bold",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: { xs: '0.75rem', md: '0.875rem' },
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {alert.user.fullname || alert.user.email}
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        gap: { xs: 0.25, md: 0.5 },
                        flexShrink: 0,
                        alignSelf: { xs: 'flex-start', sm: 'center' }
                      }}>
                        <Chip
                          label={statusInfo.label}
                          color={statusInfo.color as any}
                          size="small"
                          sx={{
                            fontSize: { xs: '0.6rem', md: '0.75rem' },
                            height: { xs: '18px', md: '24px' },
                            '& .MuiChip-label': {
                              px: { xs: 0.5, md: 1 }
                            }
                          }}
                        />
                        {alert.isBroadcasting && (
                          <Chip
                            label={translate("monitoring.live")}
                            color="error"
                            size="small"
                            sx={{
                              fontSize: { xs: '0.6rem', md: '0.75rem' },
                              height: { xs: '18px', md: '24px' },
                              '& .MuiChip-label': {
                                px: { xs: 0.5, md: 1 }
                              }
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.getContrastText(statusInfo.bgColor),
                          fontSize: { xs: '0.65rem', md: '0.75rem' },
                        }}
                      >
                        {dayjs(alert.createdAt).format("DD/MM/YYYY HH:mm")}
                      </Typography>
                    </Box>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            );
          })}
          {sortedAlerts.length === 0 && (
            <Box sx={{ p: { xs: 1, md: 2 }, textAlign: "center" }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
              >
                {translate("monitoring.noRecentEndedAlert")}
              </Typography>
            </Box>
          )}
        </Timeline>
      </Box>
    </Paper>
  );
};

export const MonitoringList: React.FC = () => {
  const { user: currentUser } = useAuth();
  const translate = useTranslate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const isXLScreen = useMediaQuery(theme.breakpoints.up('xl'));
  const { location: ipLocation } = useIPLocation();

  const [alerts, setAlerts] = useState<IAlert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPageVisible, setIsPageVisible] = useState(true);
  // Centre personnalisé (latitude, longitude) lorsqu'un événement actif est trouvé
  const [eventLocation, setEventLocation] = useState<[number, number] | null>(null);

  // Fetch enterprise alerts using direct API call
  const fetchEnterpriseAlerts = async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setIsLoading(true);
    }
    try {
      const response = await axiosInstance.get('/alert-history');
      if (response.data && Array.isArray(response.data)) {
        response.data.map((alert: IAlert) => ({
          alertId: alert.id,
          userEnterpriseId: alert.user?.entrepriseId || 'undefined',
          userName: alert.user?.fullname || alert.user?.email
        }));
        setAlerts(response.data);
      }
    } catch (error) {
      setAlerts([]);
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      }
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchEnterpriseAlerts(true);
  }, [currentUser]);

  // Récupération des événements actifs pour déterminer si l'utilisateur est securityContact
  useEffect(() => {
    const fetchActiveEventsForSecurityContact = async () => {
      try {
        if (!currentUser?.id) return;

        const response = await axiosInstance.get('/safe-events', {
          params: { isActive: 'true' },
        });

        if (Array.isArray(response.data)) {
          // Trouver le premier évènement dont l'utilisateur est securityContact
          const matchingEvent = response.data.find((event: any) => {
            if (!event.securityContacts) return false;
            return event.securityContacts.some((contact: any) => contact.id === currentUser.id);
          });

          if (matchingEvent) {
            setEventLocation([matchingEvent.latitude, matchingEvent.longitude]);
          } else {
            setEventLocation(null);
          }
        }
      } catch (error) {
        setEventLocation(null);
      }
    };

    fetchActiveEventsForSecurityContact();
  }, [currentUser]);

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = async () => {
      const isVisible = !document.hidden;
      setIsPageVisible(isVisible);

      if (isVisible) {
        await fetchEnterpriseAlerts();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Auto-refresh effect - always enabled when page is visible
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (isPageVisible) {
      intervalId = setInterval(() => fetchEnterpriseAlerts(), 10000); // 10 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPageVisible]);

  // Calculate responsive card width and count
  const { cardWidth, visibleCards } = useMemo(() => {
    if (isSmallScreen) {
      return { cardWidth: "calc((100% - 1rem) / 1.5)", visibleCards: 1.5 };
    } else if (isMediumScreen) {
      return { cardWidth: "calc((100% - 2rem) / 2.5)", visibleCards: 2.5 };
    } else if (isXLScreen) {
      return { cardWidth: "calc((100% - 4rem) / 5.5)", visibleCards: 5.5 };
    } else {
      return { cardWidth: "calc((100% - 3rem) / 4.5)", visibleCards: 4.5 };
    }
  }, [isSmallScreen, isMediumScreen, isXLScreen]);

  // Filter alerts based on broadcasting status
  const broadcastingAlerts = alerts.filter(alert => alert.isBroadcasting);
  const nonBroadcastingAlerts = alerts.filter(alert => !alert.isBroadcasting);

  // Get last 10 alerts for the map (prioritizing live alerts, then by date)
  const last10Alerts = useMemo(() => {
    // Sort all alerts: live alerts first, then by creation date (most recent first)
    const sortedAlerts = [...alerts].sort((a, b) => {
      // If one is broadcasting and the other isn't, prioritize the broadcasting one
      if (a.isBroadcasting && !b.isBroadcasting) return -1;
      if (!a.isBroadcasting && b.isBroadcasting) return 1;

      // If both have same broadcasting status, sort by creation date (most recent first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return sortedAlerts.slice(0, 10);
  }, [alerts]);

  if (isLoading) {
    return (
      <Box
        sx={{
          height: { xs: "50vh", md: "60vh" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { xs: "calc(100vh - 80px)", md: "calc(100vh - 115px)" },
        maxHeight: { xs: "calc(100vh - 80px)", md: "calc(100vh - 115px)" },
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: { xs: 1, md: 2 },
        flexShrink: 0,
      }}>
        <Typography
          variant={isSmallScreen ? "h6" : "h5"}
          sx={{
            fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
            fontWeight: "medium"
          }}
        >
          {broadcastingAlerts.length} {translate("monitoring.alertsInProgress")}
        </Typography>
      </Box>

      {/* First Row - Horizontal Slider for Alert Cards - Takes only needed space */}
      <Box sx={{ flexShrink: 0, mb: { xs: 1, md: 3 } }}>
        <Box
          sx={{
            display: "flex",
            overflowX: "auto",
            gap: { xs: 0.5, md: 1 },
            pb: 1,
            maxWidth: "100%",
            minHeight: { xs: "120px", md: "140px" },
            // Ensure smooth scrolling
            scrollBehavior: "smooth",
            // Prevent flex items from wrapping
            flexWrap: "nowrap",
            "&::-webkit-scrollbar": {
              height: { xs: 6, md: 8 },
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: 4,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#c1c1c1",
              borderRadius: 4,
              "&:hover": {
                backgroundColor: "#a8a8a8",
              },
            },
          }}
        >
          {/* Display broadcasting alerts */}
          {broadcastingAlerts.map((alert, index) => (
            <Box
              key={alert.id}
              sx={{
                minWidth: cardWidth,
                width: cardWidth,
                flexShrink: 0,
                padding: { xs: "8px", md: "12px" },
              }}
            >
              <AlertCard alert={alert} />
            </Box>
          ))}

          {/* Fill remaining slots with skeleton cards to ensure at least minimum items */}
          {Array.from({
            length: Math.max(0, Math.floor(visibleCards) - broadcastingAlerts.length),
          }).map((_, index) => (
            <Box
              key={`skeleton-${index}`}
              sx={{
                minWidth: cardWidth,
                width: cardWidth,
                flexShrink: 0,
                padding: { xs: "8px", md: "12px" },
              }}
            >
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: { xs: "100px", md: "120px" },
                }}
              >
                <CardContent sx={{ p: { xs: 1, md: 2 } }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                  >
                    {translate("monitoring.noActiveAlert")}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Second Row - Map + Timeline - Takes all remaining space */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", p: { xs: 1, md: 2 } }}
      >
        <Grid
          container
          spacing={{ xs: 1, md: 2 }}
          sx={{
            height: "100%",
            margin: 0,
            width: "100%",
          }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={8}
            lg={8}
            xl={9}
            sx={{
              height: { xs: "50%", md: "100%" },
              paddingTop: "0 !important",
              paddingLeft: "0 !important",
            }}
          >
            <Box sx={{ height: "100%", minHeight: { xs: "300px", md: "400px" } }}>
              <AlertsMap alerts={last10Alerts} eventLocation={eventLocation} ipLocation={ipLocation} />
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={4}
            lg={4}
            xl={3}
            sx={{
              height: { xs: "50%", md: "100%" },
              paddingTop: { xs: "8px !important", md: "0 !important" },
              paddingLeft: "0 !important",
            }}
          >
            <Box sx={{ height: "100%", minHeight: { xs: "250px", md: "400px" } }}>
              <AlertTimeline alerts={nonBroadcastingAlerts} />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
